import React from 'react';
import {
  Page,
  Text,
  View,
  Document,
  Image,
  StyleSheet,
  Link,
} from '@react-pdf/renderer';
import { DateTime } from 'luxon';

const BRAND_DARK = '#1e1b4b';
const BRAND_PURPLE = '#7c3aed';
const BRAND_GOLD = '#eab308';
const GRAY_100 = '#f3f4f6';
const GRAY_600 = '#4b5563';
const GRAY_800 = '#1f2937';

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    fontFamily: 'Helvetica',
  },
  /* ── Header band ── */
  header: {
    backgroundColor: BRAND_DARK,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 18,
    paddingHorizontal: 30,
  },
  logo: {
    width: 140,
  },
  headerTitle: {
    fontSize: 18,
    color: BRAND_GOLD,
    fontFamily: 'Helvetica-Bold',
    letterSpacing: 1,
  },
  /* ── Accent line ── */
  accentLine: {
    height: 4,
    backgroundColor: BRAND_PURPLE,
  },
  /* ── Body ── */
  body: {
    padding: 30,
    paddingTop: 20,
  },
  /* ── Info badges ── */
  badgesRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 18,
  },
  badge: {
    backgroundColor: GRAY_100,
    borderRadius: 4,
    padding: '6 12',
    flexDirection: 'row',
    alignItems: 'center',
  },
  badgeLabel: {
    fontSize: 8,
    color: GRAY_600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  badgeValue: {
    fontSize: 10,
    color: GRAY_800,
    fontFamily: 'Helvetica-Bold',
    marginLeft: 4,
  },
  /* ── Section heading ── */
  sectionTitle: {
    fontSize: 11,
    color: BRAND_PURPLE,
    fontFamily: 'Helvetica-Bold',
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 8,
    paddingBottom: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  /* ── Detail rows ── */
  detailRow: {
    flexDirection: 'row',
    paddingVertical: 5,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  detailRowAlt: {
    flexDirection: 'row',
    paddingVertical: 5,
    backgroundColor: '#fafafa',
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  detailLabel: {
    width: '40%',
    fontSize: 9,
    color: GRAY_600,
    paddingLeft: 6,
  },
  detailValue: {
    width: '60%',
    fontSize: 9,
    color: GRAY_800,
    fontFamily: 'Helvetica-Bold',
  },
  /* ── Amount highlight ── */
  amountBox: {
    backgroundColor: BRAND_DARK,
    borderRadius: 6,
    padding: 14,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 16,
    marginBottom: 16,
  },
  amountLabel: {
    fontSize: 11,
    color: '#a5b4fc',
    marginRight: 8,
  },
  amountValue: {
    fontSize: 22,
    color: BRAND_GOLD,
    fontFamily: 'Helvetica-Bold',
  },
  /* ── Status pill ── */
  statusContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginBottom: 14,
  },
  statusPill: {
    borderRadius: 10,
    paddingVertical: 4,
    paddingHorizontal: 14,
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  statusApproved: {
    backgroundColor: '#dcfce7',
    color: '#166534',
  },
  statusPending: {
    backgroundColor: '#fef9c3',
    color: '#854d0e',
  },
  statusRejected: {
    backgroundColor: '#fee2e2',
    color: '#991b1b',
  },
  /* ── Footer ── */
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 30,
    right: 30,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    paddingTop: 8,
  },
  footerText: {
    fontSize: 7,
    color: GRAY_600,
  },
  footerLink: {
    fontSize: 7,
    color: BRAND_PURPLE,
  },
});

const formatMonto = (value) =>
  Number(value).toLocaleString('es-AR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const getStatusStyle = (estado) => {
  if (!estado) return styles.statusPending;
  const lower = estado.toLowerCase();
  if (lower === 'approved' || lower === 'aprobado')
    return styles.statusApproved;
  if (lower === 'rejected' || lower === 'rechazado')
    return styles.statusRejected;
  return styles.statusPending;
};

const getStatusLabel = (estado) => {
  if (!estado) return 'Pendiente';
  const lower = estado.toLowerCase();
  if (lower === 'approved') return 'Aprobado';
  if (lower === 'rejected') return 'Rechazado';
  if (lower === 'pending') return 'Pendiente';
  return estado;
};

const PdfDetallesPago = ({ detallesPago }) => {
  const formattedCreatedAt = DateTime.fromISO(detallesPago.createdAt)
    .setZone('America/Argentina/Buenos_Aires')
    .toFormat('dd/MM/yyyy HH:mm:ss');
  const formattedUpdatedAt = DateTime.fromISO(detallesPago.updatedAt)
    .setZone('America/Argentina/Buenos_Aires')
    .toFormat('dd/MM/yyyy HH:mm:ss');

  const rows = [
    { label: 'ID de Pago', value: `#${detallesPago.id}` },
    { label: 'E-mail', value: detallesPago.email },
    {
      label: 'Nombre completo',
      value:
        `${detallesPago.nombre_usuario || ''} ${detallesPago.apellido_usuario || ''}`.trim() ||
        '-',
    },
    { label: 'Medio de Pago', value: detallesPago.medio_pago || '-' },
    { label: 'Descripción', value: detallesPago.descripcion || '-' },
    { label: 'ID Transacción', value: detallesPago.paymentId || '-' },
    { label: 'Fecha de Creación', value: formattedCreatedAt },
    { label: 'Última Actualización', value: formattedUpdatedAt },
  ];

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Link src="https://github.com/NicolasBorghese">
            <Image
              src="/assets/imagenesSitio/logos/CasinoPrint04Resized.png"
              alt="Logo Casino Print"
              style={styles.logo}
            />
          </Link>
          <Text style={styles.headerTitle}>Comprobante de Pago</Text>
        </View>
        <View style={styles.accentLine} />

        {/* Body */}
        <View style={styles.body}>
          {/* Quick badges */}
          <View style={styles.badgesRow}>
            <View style={styles.badge}>
              <Text style={styles.badgeLabel}>Pago Nº </Text>
              <Text style={styles.badgeValue}>{detallesPago.id}</Text>
            </View>
            <View style={styles.badge}>
              <Text style={styles.badgeLabel}>Fecha: </Text>
              <Text style={styles.badgeValue}>{formattedCreatedAt}</Text>
            </View>
          </View>

          {/* Status */}
          <View style={styles.statusContainer}>
            <Text
              style={[styles.statusPill, getStatusStyle(detallesPago.estado)]}
            >
              {getStatusLabel(detallesPago.estado)}
            </Text>
          </View>

          {/* Amount */}
          <View style={styles.amountBox}>
            <Text style={styles.amountLabel}>Monto cargado:</Text>
            <Text style={styles.amountValue}>
              ${detallesPago.monto ? formatMonto(detallesPago.monto) : '-'}
            </Text>
          </View>

          {/* Details section */}
          <Text style={styles.sectionTitle}>Detalles de la transacción</Text>
          {rows.map((row, i) => (
            <View
              key={i}
              style={i % 2 === 0 ? styles.detailRow : styles.detailRowAlt}
            >
              <Text style={styles.detailLabel}>{row.label}</Text>
              <Text style={styles.detailValue}>{row.value}</Text>
            </View>
          ))}
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>
            Documento generado automáticamente por Casino Print — Proyecto
            académico / demostración
          </Text>
          <Link src="https://github.com/NicolasBorghese">
            <Text style={styles.footerLink}>github.com/NicolasBorghese</Text>
          </Link>
        </View>
      </Page>
    </Document>
  );
};

export default PdfDetallesPago;
